import React, { useEffect, useState } from 'react';
import { Phone, MessageCircle, FileText, IndianRupee, TrendingUp, CheckCircle2, Calendar, Sparkles } from 'lucide-react';
import adminApi from '../lib/adminApi';

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="admin-stat-card" data-testid={testid}>
    <div className="admin-stat-icon"><Icon size={22} /></div>
    <div className="admin-stat-meta">
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  </div>
);

const RangeBlock = ({ title, data, testidPrefix }) => (
  <section className="admin-section">
    <h2>{title}</h2>
    <div className="admin-stats-grid">
      <StatCard icon={FileText} label="Lead Forms" value={data?.leads ?? 0} testid={`${testidPrefix}-leads`} />
      <StatCard icon={MessageCircle} label="WhatsApp Clicks" value={data?.whatsapp_clicks ?? 0} testid={`${testidPrefix}-wa`} />
      <StatCard icon={Phone} label="Phone Clicks" value={data?.phone_clicks ?? 0} testid={`${testidPrefix}-phone`} />
    </div>
  </section>
);

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [leadsAll, setLeadsAll] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    Promise.all([
      adminApi.get('/analytics/summary'),
      adminApi.get('/analytics/leads-summary?range=all'),
      adminApi.get('/insights/leads'),
    ])
      .then(([s, l, i]) => {
        if (!mounted) return;
        setSummary(s.data);
        setLeadsAll(l.data);
        setInsights(i.data);
      })
      .catch((e) => mounted && setError(e.response?.data?.detail || 'Failed to load analytics'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) return <div className="admin-loading">Loading dashboard…</div>;
  if (error) return <div className="admin-error">{error}</div>;

  const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Dashboard</h1>
        <p className="admin-page-sub">Live snapshot of o2nailshyd.com performance</p>
      </header>

      <section className="admin-section">
        <h2>Lifetime Leads & Revenue</h2>
        <div className="admin-stats-grid">
          <StatCard icon={FileText} label="Total Leads" value={leadsAll?.total_leads ?? 0} testid="kpi-total-leads" />
          <StatCard icon={CheckCircle2} label="Visited Leads" value={leadsAll?.visited_leads ?? 0} testid="kpi-visited" />
          <StatCard icon={IndianRupee} label="Total Revenue" value={inr(leadsAll?.total_revenue)} testid="kpi-revenue" />
          <StatCard icon={TrendingUp} label="Avg Revenue / Lead" value={inr(leadsAll?.avg_revenue_per_lead)} testid="kpi-arpl" />
        </div>
      </section>

      <RangeBlock title="Today" data={summary?.today} testidPrefix="today" />
      <RangeBlock title="Last 7 Days" data={summary?.last_7_days} testidPrefix="d7" />
      <RangeBlock title="Last 30 Days" data={summary?.last_30_days} testidPrefix="d30" />

      {insights && (
        <section className="admin-section">
          <h2>Smart Insights — Last 90 Days</h2>
          <div className="admin-stats-grid">
            <StatCard
              icon={Calendar}
              label="Top Performing Day"
              value={insights.top_day?.name || '—'}
              sub={insights.top_day ? `${insights.top_day.count} leads on ${insights.top_day.name}s` : 'Need more data'}
              testid="ins-top-day"
            />
            <StatCard
              icon={Sparkles}
              label="Best Acquisition Source"
              value={insights.top_source?.name || '—'}
              sub={insights.top_source ? `${insights.top_source.count} leads from this source` : 'Need more data'}
              testid="ins-top-source"
            />
            <StatCard
              icon={TrendingUp}
              label="Total Leads (90d)"
              value={insights.total_leads}
              sub="Rolling window for insights"
              testid="ins-total"
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
